import React from 'react';
import cx from 'classnames';

import { ColorToken } from '../../theme/tokens/color';

export interface SocialIconProps {
  colour?: ColorToken;
  hoverColor?: ColorToken;
  size?: 'small' | 'large';
}

/** Maps theme ColorToken to Tailwind fill class for hover (matches tailwind.config.js colors). */
const HOVER_COLOR_TO_FILL_CLASS: Partial<Record<ColorToken, string>> = {
  socialIconHover: 'group-hover:fill-navy',
  socialIcon: 'group-hover:fill-mid-blue',
  socialIconLight: 'group-hover:fill-white',
  socialIconLightHover: 'group-hover:fill-black',
  socialIconSupplementary: 'group-hover:fill-teal',
  brand: 'group-hover:fill-spearmint',
  white: 'group-hover:fill-white',
  black: 'group-hover:fill-black',
  headerLinkHover: 'group-hover:fill-green',
  inlineAnchorHover: 'group-hover:fill-green',
  buttonHover: 'group-hover:fill-green',
  defaultText: 'group-hover:fill-black',
  secondaryText: 'group-hover:fill-text'
};

const DEFAULT_HOVER_FILL_CLASS = 'group-hover:fill-teal';

function addHoverFillToSocialIconPath(
  node: React.ReactNode,
  hoverFillClass: string
): React.ReactNode {
  if (!React.isValidElement(node)) return node;
  const child = node as React.ReactElement;
  if (
    child.type === 'path' &&
    typeof child.props.className === 'string' &&
    child.props.className.includes('social-icon')
  ) {
    return React.cloneElement(child, {
      className: cx(child.props.className, hoverFillClass)
    });
  }
  if (child.props?.children) {
    return React.cloneElement(child, {
      children: React.Children.map(child.props.children, (c) =>
        addHoverFillToSocialIconPath(c, hoverFillClass)
      )
    });
  }
  return child;
}

function IconWrapper({
  hoverColor,
  children,
  ...props
}: SocialIconProps & { children: React.ReactNode }) {
  const hoverFillClass =
    (hoverColor && HOVER_COLOR_TO_FILL_CLASS[hoverColor as ColorToken]) ??
    DEFAULT_HOVER_FILL_CLASS;

  return (
    <span className="social-icon-wrapper group" {...props}>
      {React.Children.map(children, (child) =>
        addHoverFillToSocialIconPath(child, hoverFillClass)
      )}
    </span>
  );
}

export const FacebookIcon = ({
  colour = 'socialIcon',
  hoverColor = 'socialIconHover',
  size
}: SocialIconProps) => (
  <IconWrapper
    colour={colour}
    hoverColor={hoverColor}
    size={size}
    data-testid="facebook-icon"
  >
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className="social-icon"
        d="M21.7325 11.0331C21.7325 5.08879 16.9111 0.333374 11.0328 0.333374C5.1546 0.333374 0.26709 5.15484 0.26709 11.0331C0.26709 16.3829 4.16389 20.8081 9.3156 21.6667V14.1373H6.60765V11.0331H9.3156V8.65536C9.3156 5.94741 10.9007 4.49437 13.3445 4.49437C14.5334 4.49437 15.7222 4.69251 15.7222 4.69251V7.33441H14.4013C13.0803 7.33441 12.684 8.19302 12.684 8.98559V10.967H15.6562L15.1938 14.0712H12.684V21.6007C17.8357 20.8741 21.7325 16.449 21.7325 11.0331Z"
        fill="#00D9B4"
      />
    </svg>
  </IconWrapper>
);

export const YouTubeIcon = ({
  colour = 'socialIcon',
  hoverColor = 'socialIconHover',
  size
}: SocialIconProps) => (
  <IconWrapper colour={colour} hoverColor={hoverColor} size={size}>
    <svg
      width="27"
      height="26"
      viewBox="0 0 27 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className="social-icon"
        d="M13.5667 0C16.9949 0 17.3844 0 18.7869 0.0779137C20.1114 0.155827 21.0464 0.389569 21.8255 0.701223C22.6826 1.01288 23.3838 1.48036 24.085 2.18158C24.7862 2.88281 25.2537 3.58403 25.5654 4.44108C25.877 5.22022 26.1108 6.15518 26.1887 7.47972C26.2666 8.80425 26.2666 9.27173 26.2666 12.6999C26.2666 16.1281 26.2666 16.5177 26.1887 17.9202C26.1108 19.2447 25.877 20.1797 25.5654 20.9588C25.2537 21.8158 24.7862 22.5171 24.085 23.2183C23.3838 23.9195 22.6826 24.387 21.8255 24.6986C21.0464 25.0103 20.1114 25.244 18.7869 25.322C17.4623 25.3999 16.9949 25.3999 13.5667 25.3999C10.1385 25.3999 9.74889 25.3999 8.34644 25.322C7.02191 25.244 6.08695 25.0103 5.30781 24.6986C4.45076 24.387 3.74953 23.9195 3.04831 23.2183C2.34709 22.5171 1.87961 21.8158 1.56795 20.9588C1.2563 20.1797 1.02255 19.2447 0.944641 17.9202C0.944641 16.4398 0.944641 15.9723 0.944641 12.622C0.944641 9.19382 0.944641 8.80425 1.02255 7.4018C1.10047 6.07727 1.33421 5.1423 1.64586 4.36317C1.95752 3.50612 2.425 2.80489 3.12622 2.10367C3.82745 1.40245 4.52867 0.934965 5.38572 0.62331C6.16486 0.311655 7.09982 0.0779137 8.42436 0C9.67098 0 10.1385 0 13.5667 0Z"
        fill="white"
      />
      <path
        d="M23.4617 7.55763C23.228 6.62266 22.5267 5.92144 21.5918 5.6877C19.9556 5.22022 13.4887 5.22021 13.4887 5.22021C13.4887 5.22021 7.02191 5.22022 5.38572 5.6877C4.52867 5.92144 3.82745 6.62266 3.51579 7.55763C3.04831 9.19381 3.04831 12.5441 3.04831 12.5441C3.04831 12.5441 3.04831 15.9723 3.51579 17.5306C3.74953 18.4655 4.45076 19.1668 5.38572 19.4005C7.02191 19.868 13.4887 19.868 13.4887 19.868C13.4887 19.868 19.9556 19.868 21.5918 19.4005C22.4488 19.1668 23.15 18.4655 23.4617 17.5306C23.9292 15.8944 23.9292 12.5441 23.9292 12.5441C23.9292 12.5441 23.8513 9.19381 23.4617 7.55763ZM11.3851 15.6607V9.50547L16.7611 12.622L11.3851 15.6607Z"
        fill="#1D252C"
      />
      <defs>
        <clipPath>
          <rect x="0.944641" width="25.1661" height="25.1661" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </IconWrapper>
);

export const YouTube20Icon = ({
  colour = 'socialIcon',
  hoverColor = 'socialIconHover',
  size
}: SocialIconProps) => (
  <IconWrapper colour={colour} hoverColor={hoverColor} size={size}>
    <svg
      width="22"
      height="16"
      viewBox="0 0 22 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className="social-icon"
        d="M11.0679 0.611084H11.1849C12.2658 0.615029 17.7422 0.654475 19.2188 1.05156C19.6652 1.17275 20.0719 1.40897 20.3984 1.73662C20.7249 2.06427 20.9597 2.47186 21.0793 2.91867C21.2121 3.41832 21.3055 4.0797 21.3686 4.76212L21.3817 4.89886L21.4107 5.24073L21.4212 5.37747C21.5066 6.57926 21.5172 7.70479 21.5185 7.95067V8.04928C21.5172 8.30436 21.5053 9.50615 21.4107 10.7579L21.4001 10.896L21.3883 11.0327C21.3226 11.7848 21.2253 12.5317 21.0793 13.0813C20.9597 13.5281 20.7249 13.9357 20.3984 14.2633C20.0719 14.591 19.6652 14.8272 19.2188 14.9484C17.6935 15.3586 11.8963 15.3875 11.0929 15.3889H10.9062C10.4999 15.3889 8.8195 15.381 7.05758 15.3205L6.83405 15.3126L6.71966 15.3073L6.49481 15.2981L6.26997 15.2889C4.81047 15.2245 3.42066 15.1206 2.78032 14.9471C2.33409 14.826 1.92743 14.59 1.60095 14.2626C1.27447 13.9352 1.0396 13.5278 0.919784 13.0813C0.773834 12.533 0.676534 11.7848 0.61079 11.0327L0.600272 10.8947L0.589753 10.7579C0.524487 9.86697 0.488528 8.97413 0.481934 8.08084L0.481934 7.91911C0.484563 7.63641 0.495082 6.65947 0.566085 5.58128L0.575289 5.44585L0.579234 5.37747L0.589753 5.24073L0.61868 4.89886L0.631828 4.76212C0.694942 4.0797 0.788297 3.41701 0.921099 2.91867C1.04071 2.47186 1.2755 2.06427 1.60199 1.73662C1.92848 1.40897 2.33525 1.17275 2.78163 1.05156C3.42197 0.880632 4.81179 0.775442 6.27129 0.709699L6.49481 0.700495L6.72097 0.692606L6.83405 0.688661L7.05889 0.679457C8.31026 0.639227 9.56214 0.616872 10.8141 0.612399L11.0679 0.611084ZM8.89707 4.8318V11.1668L14.363 8.00063L8.89707 4.8318Z"
        fill="#00D9B4"
      />
    </svg>
  </IconWrapper>
);

export const TwitterIcon = ({
  colour = 'socialIcon',
  hoverColor = 'socialIconHover',
  size
}: SocialIconProps) => (
  <IconWrapper
    colour={colour}
    hoverColor={hoverColor}
    size={size}
    data-testid="twitter-icon"
  >
    <svg
      width="26"
      height="26"
      viewBox="0 0 26 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className="social-icon"
        d="M13.5113 0C6.49904 0 0.889252 5.60979 0.889252 12.622C0.889252 19.6343 6.49904 25.244 13.5113 25.244C20.5235 25.244 26.1333 19.6343 26.1333 12.622C26.1333 5.60979 20.4456 0 13.5113 0ZM19.8223 10.2067C19.8223 10.3625 19.8223 10.4404 19.8223 10.5963C19.8223 14.5699 16.7836 19.2447 11.1739 19.2447C9.45976 19.2447 7.90149 18.7772 6.49904 17.9202C6.73278 17.9202 6.96652 17.9202 7.27818 17.9202C8.68062 17.9202 10.0052 17.4527 11.018 16.5956C9.6935 16.5956 8.60271 15.6607 8.21314 14.492C8.36897 14.492 8.60271 14.5699 8.75854 14.5699C9.07019 14.5699 9.30393 14.5699 9.53767 14.492C8.13523 14.1803 7.12235 12.9337 7.12235 11.4533C7.51192 11.6871 7.9794 11.8429 8.5248 11.8429C7.74566 11.2975 7.20026 10.3625 7.20026 9.34965C7.20026 8.80425 7.35609 8.25885 7.58983 7.79137C9.07019 9.6613 11.3297 10.83 13.8229 10.9858C13.745 10.7521 13.745 10.5184 13.745 10.2846C13.745 8.64842 15.0695 7.24598 16.7836 7.24598C17.6407 7.24598 18.4198 7.63554 18.9652 8.18094C19.6665 8.02511 20.2898 7.79137 20.9131 7.47972C20.6793 8.18094 20.2119 8.80425 19.5885 9.19382C20.2119 9.1159 20.7572 8.96008 21.3026 8.72634C20.9131 9.27173 20.3677 9.73921 19.8223 10.2067Z"
        fill="white"
      />
      <defs>
        <clipPath>
          <rect x="0.889252" width="25.1661" height="25.1661" fill="white" />
        </clipPath>
      </defs>
    </svg>
  </IconWrapper>
);

export const TikTokIcon = ({
  colour = 'socialIcon',
  hoverColor = 'socialIconHover',
  size
}: SocialIconProps) => (
  <IconWrapper
    colour={colour}
    hoverColor={hoverColor}
    size={size}
    data-testid="tiktok-icon"
  >
    <svg
      width="18"
      height="20"
      viewBox="0 0 18 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className="social-icon"
        d="M10.125 1H12.3525C12.5145 1.80438 12.96 2.81913 13.7419 3.826C14.5069 4.81263 15.5216 5.5 16.875 5.5V7.75C14.9029 7.75 13.4213 6.83425 12.375 5.69238V13.375C12.375 14.4875 12.0451 15.5751 11.427 16.5001C10.8089 17.4251 9.93043 18.1461 8.9026 18.5718C7.87476 18.9976 6.74376 19.109 5.65262 18.8919C4.56148 18.6749 3.5592 18.1391 2.77253 17.3525C1.98586 16.5658 1.45013 15.5635 1.23309 14.4724C1.01604 13.3812 1.12744 12.2502 1.55318 11.2224C1.97892 10.1946 2.69989 9.31607 3.62492 8.69798C4.54995 8.0799 5.63748 7.75 6.75 7.75V10C6.08249 10 5.42997 10.1979 4.87495 10.5688C4.31994 10.9396 3.88736 11.4667 3.63191 12.0834C3.37646 12.7001 3.30963 13.3787 3.43985 14.0334C3.57008 14.6881 3.89152 15.2895 4.36352 15.7615C4.83552 16.2335 5.43689 16.5549 6.09157 16.6852C6.74626 16.8154 7.42486 16.7485 8.04156 16.4931C8.65826 16.2376 9.18536 15.8051 9.55621 15.2501C9.92706 14.695 10.125 14.0425 10.125 13.375V1Z"
        fill="#00D9B4"
        stroke="#00D9B4"
        strokeWidth="0.35"
      />
    </svg>
  </IconWrapper>
);

export const Twitter20Icon = ({
  colour = 'socialIcon',
  hoverColor = 'socialIconHover',
  size
}: SocialIconProps) => (
  <IconWrapper colour={colour} hoverColor={hoverColor} size={size}>
    <svg
      width="19"
      height="17"
      viewBox="0 0 19 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className="social-icon"
        d="M17.1109 4.75425C17.1109 4.95303 17.1109 5.05241 17.1109 5.25119C17.1109 10.3199 13.2343 16.2832 6.07737 16.2832C3.89054 16.2832 1.90251 15.6868 0.113281 14.5936C0.411486 14.5936 0.70969 14.5936 1.1073 14.5936C2.89652 14.5936 4.58635 13.9973 5.87857 12.904C4.18874 12.904 2.79712 11.7114 2.30011 10.2205C2.49892 10.2205 2.79712 10.3199 2.99593 10.3199C3.39353 10.3199 3.69174 10.3199 3.98994 10.2205C2.20071 9.823 0.908494 8.2328 0.908494 6.34445C1.4055 6.64261 2.00191 6.84138 2.69772 6.84138C1.70371 6.14567 1.0079 4.95303 1.0079 3.66099C1.0079 2.96528 1.2067 2.26957 1.5049 1.67325C3.39353 4.05854 6.27617 5.54935 9.45702 5.74813C9.35762 5.44996 9.35762 5.1518 9.35762 4.85364C9.35762 2.76651 11.0474 0.977539 13.2343 0.977539C14.3277 0.977539 15.3217 1.47448 16.0175 2.17019C16.9121 1.97141 17.7073 1.67325 18.5026 1.2757C18.2044 2.17019 17.6079 2.96528 16.8127 3.46222C17.6079 3.36283 18.3038 3.16406 18.9996 2.8659C18.5026 3.56161 17.8067 4.15793 17.1109 4.75425Z"
        fill="#9AB5B8"
      />
    </svg>
  </IconWrapper>
);

export const InstagramIcon = ({
  colour = 'socialIcon',
  hoverColor = 'socialIconHover',
  size
}: SocialIconProps) => (
  <IconWrapper colour={colour} hoverColor={hoverColor} size={size}>
    <svg
      width="22"
      height="22"
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className="social-icon"
        d="M11 1C8.28625 1 7.945 1.0125 6.87875 1.06C5.8125 1.11 5.08625 1.2775 4.45 1.525C3.78231 1.77541 3.17768 2.16925 2.67875 2.67875C2.16925 3.17768 1.77541 3.78231 1.525 4.45C1.2775 5.085 1.10875 5.8125 1.06 6.875C1.0125 7.94375 1 8.28375 1 11.0013C1 13.7163 1.0125 14.0563 1.06 15.1225C1.11 16.1875 1.2775 16.9137 1.525 17.55C1.78125 18.2075 2.1225 18.765 2.67875 19.3212C3.23375 19.8775 3.79125 20.22 4.44875 20.475C5.08625 20.7225 5.81125 20.8912 6.87625 20.94C7.94375 20.9875 8.28375 21 11 21C13.7163 21 14.055 20.9875 15.1225 20.94C16.1863 20.89 16.915 20.7225 17.5513 20.475C18.2185 20.2244 18.8227 19.8306 19.3212 19.3212C19.8775 18.765 20.2188 18.2075 20.475 17.55C20.7213 16.9137 20.89 16.1875 20.94 15.1225C20.9875 14.0563 21 13.7163 21 11C21 8.28375 20.9875 7.94375 20.94 6.87625C20.89 5.8125 20.7213 5.085 20.475 4.45C20.2246 3.78231 19.8308 3.17768 19.3212 2.67875C18.8223 2.16925 18.2177 1.77541 17.55 1.525C16.9125 1.2775 16.185 1.10875 15.1213 1.06C14.0538 1.0125 13.715 1 10.9975 1H11ZM10.1038 2.8025H11.0013C13.6713 2.8025 13.9875 2.81125 15.0413 2.86C16.0162 2.90375 16.5462 3.0675 16.8988 3.20375C17.365 3.385 17.6987 3.6025 18.0487 3.9525C18.3988 4.3025 18.615 4.635 18.7963 5.1025C18.9338 5.45375 19.0963 5.98375 19.14 6.95875C19.1887 8.0125 19.1987 8.32875 19.1987 10.9975C19.1987 13.6663 19.1887 13.9837 19.14 15.0375C19.0963 16.0125 18.9325 16.5413 18.7963 16.8938C18.6348 17.3274 18.3791 17.7197 18.0475 18.0425C17.6975 18.3925 17.365 18.6088 16.8975 18.79C16.5475 18.9275 16.0175 19.09 15.0413 19.135C13.9875 19.1825 13.6713 19.1938 11.0013 19.1938C8.33125 19.1938 8.01375 19.1825 6.96 19.135C5.985 19.09 5.45625 18.9275 5.10375 18.79C4.66979 18.6291 4.277 18.3738 3.95375 18.0425C3.62159 17.7195 3.36541 17.3267 3.20375 16.8925C3.0675 16.5412 2.90375 16.0113 2.86 15.0363C2.8125 13.9825 2.8025 13.6663 2.8025 10.995C2.8025 8.32375 2.8125 8.01 2.86 6.95625C2.905 5.98125 3.0675 5.45125 3.205 5.09875C3.38625 4.6325 3.60375 4.29875 3.95375 3.94875C4.30375 3.59875 4.63625 3.3825 5.10375 3.20125C5.45625 3.06375 5.985 2.90125 6.96 2.85625C7.8825 2.81375 8.24 2.80125 10.1038 2.8V2.8025ZM16.3387 4.4625C16.1812 4.4625 16.0251 4.49354 15.8795 4.55384C15.7339 4.61415 15.6017 4.70254 15.4902 4.81397C15.3788 4.9254 15.2904 5.05769 15.2301 5.20328C15.1698 5.34887 15.1388 5.50491 15.1388 5.6625C15.1388 5.82009 15.1698 5.97613 15.2301 6.12172C15.2904 6.26731 15.3788 6.3996 15.4902 6.51103C15.6017 6.62246 15.7339 6.71085 15.8795 6.77116C16.0251 6.83146 16.1812 6.8625 16.3387 6.8625C16.657 6.8625 16.9622 6.73607 17.1873 6.51103C17.4123 6.28598 17.5387 5.98076 17.5387 5.6625C17.5387 5.34424 17.4123 5.03902 17.1873 4.81397C16.9622 4.58893 16.657 4.4625 16.3387 4.4625ZM11.0013 5.865C10.3201 5.85437 9.64362 5.97936 9.01122 6.23268C8.37883 6.486 7.80314 6.86259 7.31769 7.34053C6.83223 7.81847 6.4467 8.38821 6.18355 9.01657C5.9204 9.64494 5.78488 10.3194 5.78488 11.0006C5.78488 11.6819 5.9204 12.3563 6.18355 12.9847C6.4467 13.613 6.83223 14.1828 7.31769 14.6607C7.80314 15.1387 8.37883 15.5153 9.01122 15.7686C9.64362 16.0219 10.3201 16.1469 11.0013 16.1363C12.3494 16.1152 13.6353 15.5649 14.5812 14.6041C15.5272 13.6432 16.0574 12.349 16.0574 11.0006C16.0574 9.65228 15.5272 8.35802 14.5812 7.39719C13.6353 6.43636 12.3494 5.88603 11.0013 5.865ZM11.0013 7.66625C11.439 7.66625 11.8726 7.75248 12.277 7.92002C12.6815 8.08755 13.049 8.33312 13.3586 8.64268C13.6681 8.95225 13.9137 9.31976 14.0812 9.72423C14.2488 10.1287 14.335 10.5622 14.335 11C14.335 11.4378 14.2488 11.8713 14.0812 12.2758C13.9137 12.6802 13.6681 13.0477 13.3586 13.3573C13.049 13.6669 12.6815 13.9124 12.277 14.08C11.8726 14.2475 11.439 14.3337 11.0013 14.3337C10.1171 14.3337 9.26913 13.9825 8.64393 13.3573C8.01873 12.7321 7.6675 11.8842 7.6675 11C7.6675 10.1158 8.01873 9.26788 8.64393 8.64268C9.26913 8.01748 10.1171 7.66625 11.0013 7.66625Z"
        fill="#00D9B4"
        stroke="#00D9B4"
        strokeWidth="0.2"
      />
    </svg>
  </IconWrapper>
);
